import { create } from 'zustand';
import {
  bookAppointment,
  fetchAppointments,
  checkAppointmentByMobile,
  fetchSuggestedSlots,
  updateAppointment,
  cancelAppointment,
  checkInAppointment,
  rebookAppointment,
  fetchImpactedAppointments,
  dismissImpact as dismissImpactApi,
} from '../services/api/appointmentApi';
import eventBus from '../services/realtime/EventBus';
import useVisitStore from './visitStore';
import {
  createDoctorBlock,
  listDoctorBlocks,
  deleteDoctorBlock,
  skipDoctorBlockOccurrence,
} from '../services/api/calendarBlockApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Appointment Store — Zustand state for appointment management
 *
 * Manages:
 *   • Appointment CRUD (book, list, cancel, update, rebook, check-in)
 *   • Auto-detect appointment by mobile (for register form)
 *   • Suggested time slots
 *   • Realtime event patching
 * ═══════════════════════════════════════════════════════════════════════
 */

const useAppointmentStore = create((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────
  appointments: [],
  appointmentCount: 0,
  loading: false,
  error: null,
  createdAppointment: null,    // Last created/rebooked appointment
  detectedAppointment: null,   // Auto-detected appointment for mobile
  suggestedSlots: [],
  slotsLoading: false,
  dayBookings: [],             // Selected doctor's appointments for one day (DayTimeline)
  dayBookingsLoading: false,
  dayBlocks: [],               // Selected doctor's calendar blocks (occurrences) for one day
  impactedAppointments: [],    // BOOKED appts overlapping blocking blocks (Confirm Appointments)
  impactedLoading: false,

  // ── Actions ────────────────────────────────────────────────────────

  /**
   * Book a new appointment.
   * @param {Object} payload — see appointmentApi.bookAppointment
   */
  book: async (payload) => {
    set({ loading: true, error: null, createdAppointment: null });
    try {
      const result = await bookAppointment(payload);
      // Backend returns AppointmentResponse directly (not wrapped)
      const appt = result.appointment || result;

      // Optimistically prepend to list
      set((s) => ({
        loading: false,
        createdAppointment: appt,
        appointments: [appt, ...s.appointments],
        appointmentCount: s.appointmentCount + 1,
      }));
      return result;
    } catch (err) {
      set({ loading: false, error: err.message });
      throw err;
    }
  },

  /**
   * Fetch appointments with filters.
   * @param {Object} filters — start_date, end_date, doctor_member_id, status
   */
  fetchList: async (filters = {}) => {
    set({ loading: true, error: null });
    try {
      const result = await fetchAppointments(filters);
      set({
        loading: false,
        appointments: result.appointments || [],
        appointmentCount: result.count || 0,
      });
      return result;
    } catch (err) {
      set({ loading: false, error: err.message });
      throw err;
    }
  },

  /**
   * Cancel an appointment.
   * @param {string} appointmentId
   */
  cancel: async (appointmentId) => {
    set({ loading: true, error: null });
    try {
      const result = await cancelAppointment(appointmentId);
      // Patch the item in-place
      set((s) => ({
        loading: false,
        appointments: s.appointments.map((a) =>
          a.appointment_id === appointmentId
            ? { ...a, ...result.appointment, status: 'CANCELLED' }
            : a
        ),
      }));
      return result;
    } catch (err) {
      set({ loading: false, error: err.message });
      throw err;
    }
  },

  /**
   * Update (reschedule) an appointment.
   * @param {string} appointmentId
   * @param {Object} updates — { appointment_time, appointment_date, notes }
   */
  update: async (appointmentId, updates) => {
    set({ loading: true, error: null });
    try {
      const result = await updateAppointment(appointmentId, updates);
      set((s) => ({
        loading: false,
        appointments: s.appointments.map((a) =>
          a.appointment_id === appointmentId
            ? { ...a, ...result.appointment }
            : a
        ),
      }));
      return result;
    } catch (err) {
      set({ loading: false, error: err.message });
      throw err;
    }
  },

  /**
   * Rebook a cancelled appointment (within 60-min window).
   * @param {string} appointmentId
   */
  rebook: async (appointmentId) => {
    set({ loading: true, error: null, createdAppointment: null });
    try {
      const result = await rebookAppointment(appointmentId);
      const newAppt = result.appointment;
      // Replace old cancelled item with new booked item
      set((s) => ({
        loading: false,
        createdAppointment: newAppt,
        appointments: [newAppt, ...s.appointments.filter((a) => a.appointment_id !== appointmentId)],
      }));
      return result;
    } catch (err) {
      set({ loading: false, error: err.message });
      throw err;
    }
  },

  /**
   * Check in an appointment (creates a visit).
   * @param {string} appointmentId
   * @param {boolean} consentGranted — CONSENT-CAPTURE P1/D14; forwarded verbatim.
   */
  checkIn: async (appointmentId, consentGranted) => {
    set({ loading: true, error: null });
    try {
      const result = await checkInAppointment(appointmentId, consentGranted);
      set((s) => ({
        loading: false,
        appointments: s.appointments.map((a) =>
          a.appointment_id === appointmentId
            ? { ...a, status: 'CHECKED_IN' }
            : a
        ),
      }));

      // Immediately push the new visit into visitStore so SmartWorkflowWidget
      // re-sorts the queue right away — don't wait for the WebSocket round-trip.
      // Without this, the doctor/nurse view may not reflect HAPPY_CLINIC ordering
      // for up to 30-120s (the periodic reconcile interval).
      const newVisit = result?.registration?.visit;
      if (newVisit) {
        const visitId = newVisit.id || newVisit.visit_id;
        const { dailyVisits } = useVisitStore.getState();
        const alreadyExists = dailyVisits.some(
          (v) => (v.id || v.visit_id) === visitId
        );
        if (!alreadyExists) {
          useVisitStore.setState((s) => ({
            dailyVisits: [...s.dailyVisits, newVisit],
          }));
        }
      }

      return result;
    } catch (err) {
      set({ loading: false, error: err.message });
      throw err;
    }
  },

  /**
   * Auto-detect appointment by mobile number.
   * Used in RegisterVisitForm to check if walk-in has a booked appointment.
   * @param {string} mobile — 10-digit
   */
  detectByMobile: async (mobile) => {
    try {
      const result = await checkAppointmentByMobile(mobile);
      if (result.has_appointment && result.appointment) {
        set({ detectedAppointment: { ...result.appointment, within_window: result.within_window } });
      } else {
        set({ detectedAppointment: null });
      }
      return result;
    } catch (err) {
      set({ detectedAppointment: null });
      return { has_appointment: false, appointment: null, within_window: false };
    }
  },

  /**
   * Clear the detected appointment state.
   */
  clearDetected: () => set({ detectedAppointment: null }),

  /**
   * Fetch suggested time slots for a doctor + date.
   * @param {string} doctorId
   * @param {string} date — YYYY-MM-DD (optional)
   */
  fetchSlots: async (doctorId, date) => {
    set({ slotsLoading: true });
    try {
      const result = await fetchSuggestedSlots(doctorId, date);
      // Backend returns { time: "HH:MM", existing_count } — normalize to
      // { hour, label, appointment_count, time } for the UI
      const normalized = (result.slots || []).map((s) => {
        const [h] = (s.time || '').split(':').map(Number);
        const period = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return {
          hour: h,
          time: s.time,
          label: `${h12} ${period}`,
          appointment_count: s.existing_count ?? s.appointment_count ?? 0,
        };
      });
      set({ slotsLoading: false, suggestedSlots: normalized });
      return { ...result, slots: normalized };
    } catch (err) {
      set({ slotsLoading: false, suggestedSlots: [] });
      return { slots: [] };
    }
  },

  /**
   * Fetch one doctor's appointments for a single day, for the DayTimeline.
   * Keeps a SEPARATE state key (`dayBookings`) so it never clobbers the
   * main `appointments` list used by ManageAppointmentsPage.
   * Excludes EXPIRED / NO_SHOW; keeps BOOKED + CHECKED_IN + CANCELLED
   * (CANCELLED renders struck-through on the timeline rather than vanishing).
   * @param {string} doctorId
   * @param {string} date — YYYY-MM-DD
   */
  fetchDoctorDayBookings: async (doctorId, date) => {
    if (!doctorId || !date) { set({ dayBookings: [] }); return { appointments: [] }; }
    set({ dayBookingsLoading: true });
    try {
      const result = await fetchAppointments({
        start_date: date,
        end_date: date,
        doctor_member_id: doctorId,
      });
      // Keep CANCELLED so the timeline shows it struck-through (DayTimeline renders
      // dead statuses muted + line-through; findClash skips them so the slot is
      // still bookable). Only drop the truly noisy terminal states.
      const DEAD = new Set(['EXPIRED', 'NO_SHOW']);
      const list = (result.appointments || []).filter((a) => !DEAD.has(a.status));
      set({ dayBookingsLoading: false, dayBookings: list });
      return { ...result, appointments: list };
    } catch (err) {
      set({ dayBookingsLoading: false, dayBookings: [] });
      return { appointments: [] };
    }
  },

  /**
   * Fetch a doctor's calendar blocks for one day, expanded to occurrences, and
   * shape them as BLOCK timeline entries (merged into the DayTimeline alongside
   * appointments).
   */
  fetchDoctorBlocks: async (doctorId, date) => {
    if (!doctorId || !date) { set({ dayBlocks: [] }); return []; }
    try {
      const res = await listDoctorBlocks(doctorId, date, date);
      const entries = (res.occurrences || []).map((o) => ({
        appointment_id: `block-${o.block_id}-${o.date}`,
        block_id: o.block_id,
        appointment_type: 'BLOCK',
        status: 'BLOCK',
        appointment_time: o.start_time,
        duration_minutes: o.duration_minutes,
        patient_name: o.label,
        label: o.label,
        blocking: o.blocking,
        all_day: o.all_day,
        is_recurring: o.is_recurring,
        repeat: o.repeat,
        occ_date: o.date,
        doctor_member_id: o.doctor_member_id,
      }));
      set({ dayBlocks: entries });
      return entries;
    } catch (err) {
      set({ dayBlocks: [] });
      return [];
    }
  },

  /** Create a calendar block rule for a doctor. */
  createBlock: async (doctorId, payload) => {
    return createDoctorBlock(doctorId, payload);
  },

  /** Delete a whole block series. */
  deleteBlock: async (doctorId, blockId) => {
    return deleteDoctorBlock(doctorId, blockId);
  },

  /** Skip a recurring block on one date (delete just this day). */
  skipBlock: async (doctorId, blockId, date) => {
    return skipDoctorBlockOccurrence(doctorId, blockId, date);
  },

  /**
   * Fetch impacted appointments (Confirm Appointments widget).
   * @param {Object} filters — { doctor_id }
   */
  fetchImpacted: async (filters = {}) => {
    set({ impactedLoading: true });
    try {
      const result = await fetchImpactedAppointments(filters);
      set({ impactedLoading: false, impactedAppointments: result.impacted || [] });
      return result;
    } catch (err) {
      set({ impactedLoading: false });
      return { impacted: [] };
    }
  },

  /**
   * Dismiss one impact notification (does NOT touch the appointment).
   * Optimistically removes the row; re-fetches on failure to resync.
   * @param {string} appointmentId
   * @param {string} blockId
   */
  dismissImpact: async (appointmentId, blockId) => {
    set((s) => ({
      impactedAppointments: s.impactedAppointments.filter(
        (i) => !(i.appointment_id === appointmentId && i.block_id === blockId)
      ),
    }));
    try {
      await dismissImpactApi(appointmentId, blockId);
    } catch (err) {
      get().fetchImpacted();
    }
  },

  /**
   * Clear created appointment state (used on form reset).
   */
  clearCreated: () => set({ createdAppointment: null }),

  /**
   * Clear error state.
   */
  clearError: () => set({ error: null }),

  // ── Realtime event handlers ────────────────────────────────────────

  /**
   * Handle a realtime appointment event (from WebSocket).
   * @param {Object} event — { type, payload }
   */
  handleRealtimeEvent: (event) => {
    const { type, payload } = event;
    const appt = payload?.appointment;
    if (!appt) return;

    set((s) => {
      switch (type) {
        case 'APPOINTMENT_CREATED': {
          // Add if not already present
          const exists = s.appointments.some((a) => a.appointment_id === appt.appointment_id);
          if (exists) return {};
          return {
            appointments: [appt, ...s.appointments],
            appointmentCount: s.appointmentCount + 1,
          };
        }
        case 'APPOINTMENT_UPDATED':
        case 'APPOINTMENT_CANCELLED':
        case 'APPOINTMENT_CHECKED_IN': {
          return {
            appointments: s.appointments.map((a) =>
              a.appointment_id === appt.appointment_id ? { ...a, ...appt } : a
            ),
          };
        }
        default:
          return {};
      }
    });
  },
}));

// ── Wire EventBus → store for realtime appointment events ────────────
const APPOINTMENT_EVENTS = [
  'APPOINTMENT_CREATED',
  'APPOINTMENT_UPDATED',
  'APPOINTMENT_CANCELLED',
  'APPOINTMENT_CHECKED_IN',
];

APPOINTMENT_EVENTS.forEach((eventType) => {
  eventBus.on(eventType, (event) => {
    if (event.source === 'local') return;
    useAppointmentStore.getState().handleRealtimeEvent(event);
  });
});

export default useAppointmentStore;
